import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authApi } from '../../../assets/api/services/authApi';
import { PRODUCT_NAME } from '../../../assets/constants/brand';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { PostAuthRedirectContext } from '../../../assets/contexts/postAuthRedirectContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import {
  requestTelegramBiometricAuth,
  useTelegram,
} from '../../../assets/hooks/useTelegram';
import { clearPasscode, getRegistration } from '../../../assets/services/auth';
import { signBiometricPayload } from '../../../assets/services/biometricCrypto';
import { getCards, getPostUnlockPath, refreshCards } from '../../../assets/services/cards';
import { getApiDetail } from '../../../assets/services/loginFlow';
import { markUnlockSessionReady } from '../../../assets/services/unlockSession';
import PinDots from '../PinDots/PinDots';
import PinPage from '../PinPage/PinPage';
import PinPad from '../PinPad/PinPad';
import { usePinEntry } from '../usePinEntry/usePinEntry';
import { POST_UNLOCK_BIOMETRIC_PROMPT_STATE_KEY } from './PostUnlockBiometricSheet';
import TelegramUserAvatar from '../../shared/TelegramUserAvatar/TelegramUserAvatar';
import './UnlockPasscode.css';

const UNLOCK_PROCESSING_MS = 1000;
const MAX_TRIES_EXCEEDED_DETAIL = 'Max tries exceeded';
const BIOMETRIC_LOGIN_REASON = `Unlock ${PRODUCT_NAME}`;
let lastAutoBiometricAttemptKey = '';

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function getUnlockUserId(currentUser, registration) {
  return currentUser?._id
    ?? currentUser?.id
    ?? currentUser?.user_id
    ?? registration?.currentUser?._id
    ?? registration?.currentUser?.id
    ?? registration?.currentUser?.user_id
    ?? registration?.forgotPasswordUser?._id
    ?? registration?.forgotPasswordUser?.id
    ?? registration?.forgotPasswordUser?.user_id
    ?? '';
}

async function signBiometricLoginPayload({
  currentUser,
  deviceId,
  privateKeyToken,
  registration,
  token,
}) {
  const timestamp = Math.floor(Date.now() / 1000);
  const userId = String(getUnlockUserId(currentUser, registration));
  const normalizedDeviceId = String(deviceId || '');
  const signedPayload = `tg_login ${userId} ${normalizedDeviceId} ${timestamp}`;
  const { signature } = await signBiometricPayload({
    privateKeyToken,
    payload: signedPayload,
  });

  return {
    signature,
    device_id: normalizedDeviceId,
    token,
    timestamp,
  };
}

function getUnlockToken(currentUser, registration) {
  return currentUser?.token
    ?? registration?.currentUser?.token
    ?? registration?.forgotPasswordUser?.token
    ?? registration?.token
    ?? registration?.forgotPasswordToken
    ?? null;
}

function getUnlockBiometryDevicePresent(currentUser, registration) {
  return currentUser?.biometry_device_present
    ?? currentUser?.biometryDevicePresent
    ?? registration?.currentUser?.biometry_device_present
    ?? registration?.currentUser?.biometryDevicePresent
    ?? registration?.forgotPasswordUser?.biometry_device_present
    ?? registration?.forgotPasswordUser?.biometryDevicePresent
    ?? false;
}

function getAutoBiometricAttemptKey({ biometric, currentUser, registration, unlockToken }) {
  return [
    getUnlockUserId(currentUser, registration),
    biometric?.deviceId || '',
    unlockToken || '',
  ].join(':');
}

function shouldPromptBiometricEnrollment(biometric, user) {
  return Boolean(
    user
    && biometric?.isBiometricAvailable === true
    && biometric?.isAccessRequested === false
  );
}

function buildPasswordResetUser(currentUser, registration, token) {
  return {
    ...registration?.forgotPasswordUser,
    ...registration?.currentUser,
    ...currentUser,
    token,
    email:
      currentUser?.email
      ?? registration?.currentUser?.email
      ?? registration?.forgotPasswordUser?.email
      ?? registration?.email,
    phone:
      currentUser?.phone
      ?? currentUser?.phoneNumber
      ?? registration?.currentUser?.phone
      ?? registration?.forgotPasswordUser?.phone
      ?? registration?.phone
      ?? registration?.phoneNumber,
    reg_data: {
      ...(registration?.forgotPasswordUser?.reg_data ?? {}),
      ...(registration?.currentUser?.reg_data ?? {}),
      ...(currentUser?.reg_data ?? {}),
      stages: {
        ...(registration?.forgotPasswordUser?.reg_data?.stages ?? {}),
        ...(registration?.currentUser?.reg_data?.stages ?? {}),
        ...(currentUser?.reg_data?.stages ?? {}),
        is_system_allowed: false,
      },
    },
  };
}

function UnlockPasscode({
  authService = authApi,
  disableMissingTokenRedirect = false,
}) {
  const navigate = useNavigate();
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);
  const { consumePostAuthRedirect } = useContext(PostAuthRedirectContext);
  const { pin } = useContext(TranslationContext);
  const { biometric, webApp } = useTelegram();
  const isMountedRef = useRef(true);
  const didUnlockRef = useRef(false);
  const didAutoBiometricAttemptRef = useRef(false);
  const isCompletingUnlockRef = useRef(false);
  const [hasError, setHasError] = useState(false);
  const [errorPulse, setErrorPulse] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const registration = getRegistration();
  const unlockToken = getUnlockToken(currentUser, registration);
  const canUnlock = Boolean(unlockToken);
  const canUseBiometric = biometric?.isAccessGranted === true
    && biometric?.isBiometricTokenSaved === true
    && getUnlockBiometryDevicePresent(currentUser, registration) === true;

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (disableMissingTokenRedirect || didUnlockRef.current) {
      return;
    }

    if (!canUnlock) {
      navigate('/onboarding', { replace: true });
    }
  }, [canUnlock, disableMissingTokenRedirect, navigate]);

  const navigateAfterUnlock = useCallback(async (
    nextUnlockedUser,
    { showBiometricPrompt = false } = {}
  ) => {
    if (isCompletingUnlockRef.current) {
      return;
    }

    isCompletingUnlockRef.current = true;
    let cards = [];

    try {
      cards = await refreshCards({ limit: 25 });
    } catch (error) {
      cards = error?.statusCode === 404 ? [] : getCards();
    }

    const nextPath = getPostUnlockPath(nextUnlockedUser) === '/home' || cards.length > 0
      ? '/home'
      : '/cards/new';
    const targetPath = consumePostAuthRedirect(nextPath);

    navigate(targetPath === '/cards/new' ? '/cards/new?hideBack=1' : targetPath, {
      replace: true,
      state: {
        fromUnlock: true,
        hideBack: targetPath === '/cards/new',
        ...(showBiometricPrompt
          ? { [POST_UNLOCK_BIOMETRIC_PROMPT_STATE_KEY]: true }
          : {}),
      },
    });
  }, [consumePostAuthRedirect, navigate]);

  const finishUnlock = useCallback(async (nextCurrentUser, { promptBiometrics = false } = {}) => {
    didUnlockRef.current = true;
    const nextUnlockedUser = nextCurrentUser ?? currentUser;

    if (nextCurrentUser) {
      setCurrentUser(nextCurrentUser);
    }

    markUnlockSessionReady();
    await navigateAfterUnlock(nextUnlockedUser, {
      showBiometricPrompt: promptBiometrics
        && shouldPromptBiometricEnrollment(biometric, nextUnlockedUser),
    });
  }, [
    biometric,
    currentUser,
    navigateAfterUnlock,
    setCurrentUser,
  ]);

  const handleComplete = useCallback(
    async (password) => {
      setIsChecking(true);
      setIsProcessing(true);
      const minProcessingDelay = delay(UNLOCK_PROCESSING_MS);

      try {
        const nextCurrentUser = await authService.validatePassword({
          password,
          token: unlockToken,
        });
        await minProcessingDelay;

        if (!isMountedRef.current) {
          return true;
        }

        await finishUnlock(nextCurrentUser, { promptBiometrics: true });
        return true;
      } catch (error) {
        await minProcessingDelay;

        if (!isMountedRef.current) {
          return false;
        }

        if (getApiDetail(error) === MAX_TRIES_EXCEEDED_DETAIL) {
          const nextRegistration = clearPasscode() ?? getRegistration();
          const passwordResetUser = buildPasswordResetUser(
            currentUser,
            nextRegistration,
            unlockToken
          );

          setCurrentUser(passwordResetUser);
          navigate('/forgot-pin', {
            replace: true,
            state: { user: passwordResetUser },
          });
          return true;
        }

        setHasError(true);
        setErrorPulse((value) => value + 1);
        toast.error(pin.incorrectPasscode);
        setIsProcessing(false);
        setIsChecking(false);
        return false;
      }
    },
    [
      authService,
      currentUser,
      finishUnlock,
      navigate,
      pin.incorrectPasscode,
      setCurrentUser,
      unlockToken,
    ]
  );

  const { digits, append, remove } = usePinEntry(4, handleComplete);

  const handleBiometricLogin = useCallback(async ({ silent = false } = {}) => {
    if (!canUseBiometric || digits.length > 0 || isChecking || didUnlockRef.current) {
      return false;
    }

    setIsChecking(true);
    setIsProcessing(true);

    try {
      const authResult = await requestTelegramBiometricAuth(webApp, {
        reason: BIOMETRIC_LOGIN_REASON,
      });

      if (!authResult?.isAuthenticated || !authResult?.biometricToken) {
        if (!silent) {
          toast.error(pin.biometricAuthFailed);
        }

        return false;
      }

      const registration = getRegistration();
      const payload = await signBiometricLoginPayload({
        currentUser,
        deviceId: biometric?.deviceId,
        privateKeyToken: authResult.biometricToken,
        registration,
        token: unlockToken,
      });
      const nextCurrentUser = await authService.biometricLogin(payload);

      if (!isMountedRef.current) {
        return true;
      }

      await finishUnlock(nextCurrentUser || currentUser);
      return true;
    } catch (error) {
      if (!silent) {
        toast.error(pin.biometricUnlockFailed);
      }

      return false;
    } finally {
      if (isMountedRef.current && !didUnlockRef.current) {
        setIsProcessing(false);
        setIsChecking(false);
      }
    }
  }, [
    authService,
    biometric?.deviceId,
    canUseBiometric,
    currentUser,
    digits.length,
    finishUnlock,
    isChecking,
    pin,
    unlockToken,
    webApp,
  ]);

  useEffect(() => {
    if (
      didAutoBiometricAttemptRef.current
      || !canUnlock
      || !canUseBiometric
      || didUnlockRef.current
      || digits.length > 0
      || isChecking
    ) {
      return;
    }

    const nextAttemptKey = getAutoBiometricAttemptKey({
      biometric,
      currentUser,
      registration: getRegistration(),
      unlockToken,
    });

    if (lastAutoBiometricAttemptKey === nextAttemptKey) {
      return;
    }

    didAutoBiometricAttemptRef.current = true;
    lastAutoBiometricAttemptKey = nextAttemptKey;
    handleBiometricLogin({ silent: true });
  }, [
    biometric,
    canUnlock,
    canUseBiometric,
    currentUser,
    digits.length,
    handleBiometricLogin,
    isChecking,
    unlockToken,
  ]);

  const handleDigit = useCallback(
    (digit) => {
      if (hasError) {
        setHasError(false);
      }
      append(digit);
    },
    [append, hasError]
  );

  if (!canUnlock) {
    return null;
  }

  return (
    <PinPage className="unlock-passcode">
      <div className="pin-page__body">
        <TelegramUserAvatar className="unlock-passcode__avatar" />

        <div className="pin-page__intro unlock-passcode__intro">
          <h1 className="pin-page__title">{pin.unlockTitle}</h1>
          <p className="pin-page__subtitle">{pin.unlockSubtitle}</p>
        </div>

        <PinDots
          length={4}
          filledCount={digits.length}
          hasError={hasError}
          errorKey={errorPulse}
          isProcessing={isProcessing && digits.length === 4 && !hasError}
          className="pin-dots--with-feedback"
        />
        <div className="unlock-passcode__pad-group">
          <button
            type="button"
            className="unlock-passcode__forgot"
            onClick={() => navigate('/forgot-pin')}
          >
            {pin.forgotPinButton}
          </button>
          <PinPad
            onDigit={handleDigit}
            onDelete={remove}
            onBiometric={canUseBiometric ? handleBiometricLogin : null}
            showDelete={digits.length > 0}
            disabled={isChecking}
          />
        </div>
      </div>

    </PinPage>
  );
}

export default UnlockPasscode;
